<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from '#imports'
import { Button, Input } from 'refresh-ui'
import SearchIcon from '@/components/atoms/Icons/Search.vue'

const props = defineProps({
  initialQuery: {
    type: String,
    default: '',
  },
  isLoading: Boolean,
})
const { t } = useI18n()
const emit = defineEmits(['focus', 'blur', 'submit']) // Define emitted events for focus/blur to control dropdown outside
const hasSetInitialQuery = ref(false) // Ensure we only refine once
const refineFn = ref<(query: string) => void>()

watchEffect(() => {
  if (!hasSetInitialQuery.value && refineFn.value && props.initialQuery) {
    refineFn.value(props.initialQuery)
    hasSetInitialQuery.value = true
  }
})
</script>

<template>
  <div class="flex justify-center">
    <div class="mx-auto w-full">
      <!-- Algolia search box wrapper -->
      <ais-search-box class="w-full">
        <!-- Scoped slot with Algolia props -->
        <template #default="{ currentRefinement, refine }">
          <div v-if="!hasSetInitialQuery && (refineFn = refine)" style="display: none" />
          <div class="relative flex flex-1 flex-col md:flex-row md:items-end">
            <!-- Search input + icon + button wrapper -->
            <div class="relative flex w-full items-center">
              <SearchIcon
                class="absolute left-2 z-10 h-6 w-6 text-blue transition-colors peer-focus:text-blue"
              />

              <!-- Main search input -->
              <Input
                :value="currentRefinement"
                :placeholder="t('search')"
                type="search"
                name="search"
                style-label="hidden"
                style-input="peer !pl-10 pr-10 h-12 w-full placeholder-blue rounded-l-md rounded-r-none border border-slate-200 shadow-sm"
                @focus="emit('focus')"
                @keydown.enter.prevent="emit('submit', currentRefinement)"
                @input="(event: any) => refine(event.target.value)"
              >
                <template #right>
                  <button
                    v-if="currentRefinement"
                    aria-label="Clear search"
                    class="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    @click="
                      () => {
                        refine('')
                        emit('blur')
                      }
                    "
                  >
                    ✕
                  </button>
                </template>
              </Input>
              <Button
                :is-loading="isLoading"
                :title="t('search')"
                type-button="button"
                class="ml-2 h-12 rounded-l-none rounded-r-md border-l-0 px-6 text-white shadow-md"
                @click="emit('submit', currentRefinement)"
              />
            </div>
          </div>
        </template>
      </ais-search-box>
    </div>
  </div>
</template>

<style scoped>
input::placeholder {
  color: #94a3b8; /* Tailwind slate-400 */
}
</style>
